import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import sbhrpContent from '../data/sbhrpContent';
import sbhrpLabMembers from '../data/sbhrpLabMembers';
import useTranslation from '../utils/useTranslation';
import Header from './Header';
import Footer from './Footer';

const SBHRP_META_ICONS = {
  duration: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  budget: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  funding: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  sdg: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
};

const SBHRP_FEATURE_ICONS = {
  awareness: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" />
    </svg>
  ),
  support: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3v18h18" />
      <path d="m19 9-5 5-4-4-3 3" />
    </svg>
  ),
  tools: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  ),
  inclusion: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  dialogue: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 10h10M7 13.5h6" />
      <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
    </svg>
  ),
  workshop: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 3h20" />
      <path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3" />
      <path d="m7 21 5-5 5 5" />
    </svg>
  ),
  guide: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  operational: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  incentives: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="6" />
      <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
    </svg>
  ),
  lab: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 2v7.31" />
      <path d="M14 2v7.31" />
      <path d="M8.5 2h7" />
      <path d="M14 9.3a6 6 0 1 1-4 0" />
      <path d="M5.52 16h12.96" />
    </svg>
  ),
  legal: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
      <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
      <path d="M7 21h10" />
      <path d="M12 3v18" />
    </svg>
  ),
  participants: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  women: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" strokeOpacity="0.28" />
      <circle
        cx="12"
        cy="12"
        r="10"
        strokeDasharray="36.42 26.41"
        transform="rotate(-90 12 12)"
      />
    </svg>
  ),
  chambers: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
    </svg>
  ),
  companies: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 21h18" />
      <path d="M5 21V7l8-4v18" />
      <path d="M19 21V11l-6-4" />
      <path d="M9 9v.01M9 12v.01M9 15v.01M9 18v.01" />
    </svg>
  ),
  activities: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
      <path d="m9 16 2 2 4-4" />
    </svg>
  ),
  beneficiaries: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
      <path d="M18 11h5M20.5 8v6" />
    </svg>
  ),
  handbook: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  ),
  ethics: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  ),
  toolkit: (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      <rect x="2" y="8" width="20" height="12" rx="2" />
    </svg>
  ),
};

const VISION_ICON = (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const GOAL_ICON = (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const META_ORDER = ['funding', 'budget', 'duration'];

function SbhrpSectionHeading({ title, light = false }) {
  return (
    <div className={`sbhrp-section-head${light ? ' sbhrp-section-head--light' : ''}`}>
      <h2 className="sbhrp-section-title">{title}</h2>
    </div>
  );
}

function SbhrpFeatureGrid({ items, layout }) {
  const className = layout
    ? `sbhrp-feature-grid sbhrp-feature-grid--${layout}`
    : 'sbhrp-feature-grid';

  return (
    <ul className={className}>
      {items.map((item) => (
        <li key={item.text.slice(0, 32)} className="sbhrp-feature-card">
          <span className="sbhrp-feature-icon">{SBHRP_FEATURE_ICONS[item.icon]}</span>
          <p className="sbhrp-feature-text">{item.text}</p>
        </li>
      ))}
    </ul>
  );
}

function SbhrpStatGrid({ items }) {
  return (
    <ul className="sbhrp-stat-grid">
      {items.map((item) => (
        <li key={item.label} className="sbhrp-stat-card">
          <span className="sbhrp-stat-icon">{SBHRP_FEATURE_ICONS[item.icon]}</span>
          <span className="sbhrp-stat-value">{item.value}</span>
          <span className="sbhrp-stat-label">{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

function SbhrpToolsGrid({ items }) {
  return (
    <ul className="sbhrp-tools-grid">
      {items.map((item, index) => (
        <li key={item.title} className={`sbhrp-tool-card sbhrp-tool-card--${index % 5}`}>
          <span className="sbhrp-tool-icon">{SBHRP_FEATURE_ICONS[item.icon]}</span>
          <span className="sbhrp-tool-title">{item.title}</span>
        </li>
      ))}
    </ul>
  );
}

export default function SbhrpPage() {
  const { lang } = useTranslation();
  const navigate = useNavigate();
  const content = sbhrpContent[lang] || sbhrpContent.ar;
  const labMembersContent = sbhrpLabMembers[lang] || sbhrpLabMembers.ar;
  const heroImage = `${process.env.PUBLIC_URL}/human_right_bg.png`;
  const visionIcon = `${process.env.PUBLIC_URL}/human_right_icon_1.png`;
  const orderedMeta = META_ORDER.map((type) => content.meta.find((item) => item.type === type)).filter(Boolean);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleNavigate = (sectionId) => {
    if (sectionId === 'home') {
      navigate('/');
      return;
    }
    if (sectionId === 'about' || sectionId === 'laws') return;
    navigate('/', { state: { scrollTo: sectionId } });
  };

  return (
    <div className="news-list-page sbhrp-page">
      <Header active="home" onNavigate={handleNavigate} />

      <section
        className="sbhrp-hero"
        style={{ backgroundImage: `url(${heroImage})` }}
        aria-labelledby="sbhrp-hero-title"
      >
        <div className="sbhrp-hero-overlay" aria-hidden="true" />
        <div className="container sbhrp-hero-inner">
          <h1 id="sbhrp-hero-title" className="sbhrp-hero-title">
            {content.title}
          </h1>
          <p className="sbhrp-hero-lead">{content.homeExcerpt}</p>
        </div>
      </section>

      <div className="container sbhrp-info-wrap">
        <div className="sbhrp-info-bar">
          <article className="sbhrp-info-item sbhrp-info-item--sdg">
            <div className="sbhrp-info-icon">{SBHRP_META_ICONS.sdg}</div>
            <h2 className="sbhrp-info-label">{content.sdgTitle}</h2>
            <div className="sbhrp-info-sdg">
              {content.sdgImages.map((image) => (
                <img
                  key={image.src}
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  decoding="async"
                />
              ))}
            </div>
          </article>

          {orderedMeta.map((item) => (
            <article key={item.label} className={`sbhrp-info-item sbhrp-info-item--${item.type}`}>
              <div className="sbhrp-info-icon">{SBHRP_META_ICONS[item.type]}</div>
              <h2 className="sbhrp-info-label">{item.label}</h2>
              <p className="sbhrp-info-value">{item.value}</p>
            </article>
          ))}
        </div>
      </div>

      <main className="sbhrp-page-main">
        <section className="container sbhrp-vision-goal" aria-labelledby="sbhrp-vision-heading">
          <article className="sbhrp-vision-card">
            <div className="sbhrp-vision-card-head">
              <span className="sbhrp-card-icon sbhrp-card-icon--accent">{VISION_ICON}</span>
              <h2 id="sbhrp-vision-heading" className="sbhrp-vision-title">
                {content.visionTitle}
              </h2>
            </div>
            <p className="sbhrp-vision-text">{content.visionBody}</p>
            <img
              className="sbhrp-vision-art"
              src={visionIcon}
              alt=""
              loading="lazy"
              decoding="async"
            />
          </article>

          <article className="sbhrp-goal-card" aria-labelledby="sbhrp-goal-heading">
            <div className="sbhrp-goal-card-head">
              <span className="sbhrp-card-icon sbhrp-card-icon--goal">{GOAL_ICON}</span>
              <h2 id="sbhrp-goal-heading" className="sbhrp-goal-title">
                {content.generalTitle}
              </h2>
            </div>
            {content.generalBody.map((paragraph) => (
              <p key={paragraph.slice(0, 24)} className="sbhrp-goal-text">
                {paragraph}
              </p>
            ))}
          </article>
        </section>

        <section className="container sbhrp-section" aria-labelledby="sbhrp-objectives-heading">
          <SbhrpSectionHeading title={content.objectivesTitle} />
          <SbhrpFeatureGrid items={content.objectives} layout="objectives" />
        </section>

        <section className="container sbhrp-section" aria-labelledby="sbhrp-deliverables-heading">
          <SbhrpSectionHeading title={content.deliverablesTitle} />
          <SbhrpFeatureGrid items={content.deliverables} layout="deliverables" />
        </section>

        <section className="sbhrp-impact-band" aria-labelledby="sbhrp-impact-heading">
          <div className="container">
            <SbhrpSectionHeading title={content.impactTitle} light />
            <SbhrpStatGrid items={content.impact} />
          </div>
        </section>

        <section className="container sbhrp-section" aria-labelledby="sbhrp-tools-heading">
          <SbhrpSectionHeading title={content.toolsTitle} />
          <p className="sbhrp-section-intro">{content.toolsIntro}</p>
          <SbhrpToolsGrid items={content.tools} />
        </section>

        <section className="container sbhrp-lab-section" aria-labelledby="sbhrp-lab-heading">
          <div className="sbhrp-lab-copy">
            <h2 id="sbhrp-lab-heading" className="sbhrp-lab-title">
              {content.labTitle}
            </h2>
            <div className="sbhrp-lab-intro">
              {content.labParagraphs.map((paragraph) => (
                <p key={paragraph} className="sbhrp-lab-text">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="sbhrp-lab-highlights">
              <h3 className="sbhrp-lab-highlights-title">{content.labHighlightsTitle}</h3>
              <ul className="sbhrp-lab-highlights-list">
                {content.labHighlights.map((item, index) => (
                  <li key={item} className="sbhrp-lab-highlight">
                    <span className="sbhrp-lab-highlight-number" aria-hidden="true">
                      {index + 1}
                    </span>
                    <span className="sbhrp-lab-highlight-text">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="sbhrp-lab-actions">
              <Link className="sbhrp-lab-members-btn" to="/sbhrp/lab-members">
                <span className="sbhrp-lab-members-btn-label">{labMembersContent.membersButton}</span>
                <span className="sbhrp-lab-members-btn-arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" />
                    <path d="m13 6 6 6-6 6" />
                  </svg>
                </span>
              </Link>
            </div>
          </div>
        </section>

        <section className="container sbhrp-section" aria-labelledby="sbhrp-attachments-heading">
          <SbhrpSectionHeading title={content.attachmentsTitle} />
          <ul className="sbhrp-attachments">
            {content.attachments.map((item) => (
              <li key={item.href}>
                <a
                  className="sbhrp-attachment-link"
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="sbhrp-attachment-icon" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                      <path d="M14 3v5h5" />
                      <path d="M8 13h8" />
                      <path d="M8 17h5" />
                    </svg>
                  </span>
                  <span className="sbhrp-attachment-label">{item.label}</span>
                  <span className="sbhrp-attachment-type">PDF</span>
                </a>
              </li>
            ))}
          </ul>
        </section>

        <section className="container sbhrp-section sbhrp-partners-section" aria-labelledby="sbhrp-partners-heading">
          <SbhrpSectionHeading title={content.partnersTitle} />
          <p className="sbhrp-section-intro">{content.partnersIntro}</p>
          <div className="sbhrp-partners-grid">
            {content.partners.map((partner) => (
              <figure key={partner.name} className="sbhrp-partner-card">
                <div className="sbhrp-partner-logo">
                  <img src={partner.image} alt={partner.alt} loading="lazy" decoding="async" />
                </div>
                <figcaption className="sbhrp-partner-name">{partner.name}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="sbhrp-closing-band" aria-label={content.closing}>
          <div className="container">
            <p className="sbhrp-closing">{content.closing}</p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
